"use client";

import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TopSheen } from "./TopSheen";

/**
 * Single-line dark dropdown used across the flow.
 *
 * The trigger's built-in lucide chevron is reused rather than drawing another —
 * its path is already the design's 12x6 mark — resized to 24px at the
 * specified 2.5 stroke. Stays with popper positioning anchored below the field;
 * the viewport height override undoes shadcn's `h-[--radix-select-trigger-height]`,
 * which otherwise clips the list to roughly one row.
 */
export function FlowSelect({
  value,
  onChange,
  options,
  id,
  ariaLabel,
  placeholder,
  className,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  id?: string;
  ariaLabel?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <div className={cn("relative w-full", className)}>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          id={id}
          aria-label={ariaLabel}
          className="h-14 w-full cursor-pointer rounded-lg border-0 bg-[#0F1319] py-3 pl-6 pr-6 font-suisse-intl-medium text-base text-white ring-offset-0 focus:ring-0 focus:ring-offset-0 [&>svg]:h-6 [&>svg]:w-6 [&>svg]:stroke-[2.5] [&>svg]:opacity-100"
        >
          <SelectValue placeholder={placeholder ?? "Select an option"} />
        </SelectTrigger>
        <SelectContent
          position="popper"
          side="bottom"
          avoidCollisions={false}
          sideOffset={8}
          className="max-h-[320px] rounded-lg border-0 bg-[#0F1319] p-1 text-white shadow-[0px_8px_24px_rgba(0,0,0,0.45)] outline outline-1 -outline-offset-1 outline-white/10 [&_[data-radix-select-viewport]]:h-auto"
        >
          {options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              className="cursor-pointer rounded-md py-3 pr-3 font-suisse-intl-medium text-base text-white focus:bg-white/10 focus:text-white data-[state=checked]:bg-white/[0.06]"
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <TopSheen />
    </div>
  );
}
